import { type FC, type ReactNode, useState, useEffect, useRef, useCallback } from "react";
import { Icon } from "@root/shared/components/Icon";
import * as styles from "./styles.css";

const ANIMATION_DURATION = 200;

type Props = {
  isOpened: boolean;
  message: ReactNode;
  onClose?: () => void;
  className?: string;
};

export const Snackbar: FC<Props> = ({ isOpened, message, onClose, className = "" }) => {
  const [isVisible, setIsVisible] = useState(isOpened);
  const [shouldRender, setShouldRender] = useState(isOpened);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isOpened) {
      setShouldRender(true);
      setIsVisible(true);
      clearTimer();
    } else {
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => setShouldRender(false), ANIMATION_DURATION);
    }

    return clearTimer;
  }, [isOpened, clearTimer]);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  if (!shouldRender) {
    return null;
  }

  return (
    <div className={[styles.snackbar({ visible: isVisible }), className].join(" ")}>
      <span className={styles.message}>{message}</span>
      <button className={styles.closeButton} onClick={handleClose} type="button">
        <Icon name="cross-thin" />
      </button>
    </div>
  );
};
